import styled from '@emotion/styled'
import { MediaQuery, useLanguage } from '@pharmac-extranet/shared'
import {
  PharmacColumn,
  PharmacContainer,
  PharmacFieldset,
  PharmacFieldsetLegend,
  PharmacH5,
  PharmacH6,
  PharmacText,
} from '@pharmac/ui'
import { useUserCustomerDetails } from '../../features/customer/useUserCustomerDetails'
import { ResetApiKeyInput } from './ResetApiKeyInput'

const InfoColumnContainer = styled(PharmacContainer)`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-left: var(--spacing-medium);
  margin-top: var(--spacing-medium);

  ${MediaQuery.maxTablet} {
    width: 100%;
    padding-left: 0;
    display: flex;
    flex-direction: column;
  }
`

const ColumnContainer = styled(PharmacContainer)`
  width: 100%;
`

export const CustomerInfo = () => {
  const { t } = useLanguage(['extranet', 'common'])
  const { userCustomer } = useUserCustomerDetails()

  return (
    <>
      <PharmacFieldset
        marginMediumTop
        marginLargeLeft
        marginLargeRight
      >
        <PharmacFieldsetLegend>
          <PharmacH5 marginSmallRight>{t('basicInfo', { ns: 'common' })}</PharmacH5>
        </PharmacFieldsetLegend>
        <InfoColumnContainer>
          <ColumnContainer>
            <PharmacColumn>
              <PharmacH6>{t('customer.businessName', { ns: 'extranet' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.name || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('customer.businessId', { ns: 'extranet' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.businessId || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('customer.taxCode', { ns: 'extranet' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.taxCode || '-'}</PharmacText>
              </PharmacContainer>

              <PharmacH6 paddingMediumTop>{t('contactPersonName', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>
                  {userCustomer?.buyerAddressCard?.contactPersonName || '-'}
                </PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('emailAddress', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.buyerAddressCard?.emailAddress || '-'}</PharmacText>
              </PharmacContainer>
            </PharmacColumn>
          </ColumnContainer>

          <ColumnContainer>
            <PharmacColumn>
              <PharmacH6>{t('phoneNumber', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.buyerAddressCard?.phoneNumber || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('streetName', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.buyerAddressCard?.streetName || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('postCode', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.buyerAddressCard?.postCode || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('townName', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.buyerAddressCard?.townName || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('country', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.buyerAddressCard?.countryName || '-'}</PharmacText>
              </PharmacContainer>
            </PharmacColumn>
          </ColumnContainer>
        </InfoColumnContainer>
      </PharmacFieldset>
      <PharmacFieldset
        marginLargeTop
        marginLargeLeft
        marginLargeRight
      >
        <PharmacFieldsetLegend>
          <PharmacH5>{t('customer.invoicingInfo', { ns: 'extranet' })}</PharmacH5>
        </PharmacFieldsetLegend>
        <InfoColumnContainer>
          <ColumnContainer>
            <PharmacColumn>
              <PharmacH6>{t('customer.invoiceAddress', { ns: 'extranet' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>
                  {userCustomer?.invoiceRecipientOrganisationUnitNumber || '-'}
                </PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('customer.operatorId', { ns: 'extranet' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.invoiceToIntermediator || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('contactPersonName', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>
                  {userCustomer?.billingAddressCard?.contactPersonName || '-'}
                </PharmacText>
              </PharmacContainer>

              <PharmacH6 paddingMediumTop>{t('emailAddress', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.billingAddressCard?.emailAddress || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('buyingBan', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>
                  {userCustomer?.buyingBan ? t('common:yes') : t('common:no')}
                </PharmacText>
              </PharmacContainer>
            </PharmacColumn>
          </ColumnContainer>

          <ColumnContainer>
            <PharmacColumn>
              <PharmacH6>{t('phoneNumber', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.billingAddressCard?.phoneNumber || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('streetName', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.billingAddressCard?.streetName || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('postCode', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.billingAddressCard?.postCode || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('townName', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.billingAddressCard?.townName || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('country', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.billingAddressCard?.countryName || '-'}</PharmacText>
              </PharmacContainer>
            </PharmacColumn>
          </ColumnContainer>
        </InfoColumnContainer>
      </PharmacFieldset>
      <PharmacFieldset
        marginLargeTop
        marginLargeLeft
        marginLargeRight
      >
        <PharmacFieldsetLegend>
          <PharmacH5>{t('customer.deliveryInfo', { ns: 'extranet' })}</PharmacH5>
        </PharmacFieldsetLegend>
        <InfoColumnContainer>
          <ColumnContainer>
            <PharmacColumn>
              <PharmacH6>{t('contactPersonName', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>
                  {userCustomer?.deliveryAddressCard?.contactPersonName || '-'}
                </PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('emailAddress', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.deliveryAddressCard?.emailAddress || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('streetName', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.deliveryAddressCard?.streetName || '-'}</PharmacText>
              </PharmacContainer>
            </PharmacColumn>
          </ColumnContainer>

          <ColumnContainer>
            <PharmacColumn>
              <PharmacH6>{t('postCode', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.deliveryAddressCard?.postCode || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('townName', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.deliveryAddressCard?.townName || '-'}</PharmacText>
              </PharmacContainer>
              <PharmacH6 paddingMediumTop>{t('country', { ns: 'common' })}</PharmacH6>
              <PharmacContainer
                marginSmallTop
                marginMediumBottom
              >
                <PharmacText>{userCustomer?.deliveryAddressCard?.countryName || '-'}</PharmacText>
              </PharmacContainer>
            </PharmacColumn>
          </ColumnContainer>
        </InfoColumnContainer>
      </PharmacFieldset>
      {(userCustomer?.type as unknown as string) === 'pharmacy' && (
        <PharmacFieldset marginLarge>
          <PharmacFieldsetLegend>
            <PharmacH5>{t('customer.pharmacyInfo', { ns: 'extranet' })}</PharmacH5>
          </PharmacFieldsetLegend>
          <InfoColumnContainer>
            <ColumnContainer>
              <PharmacColumn>
                <PharmacH6>{t('customer.doseDeliveryContactPerson', { ns: 'extranet' })}</PharmacH6>
                <PharmacContainer
                  marginSmallTop
                  marginMediumBottom
                >
                  <PharmacText>{userCustomer?.doseDeliveryContactPersonName || '-'}</PharmacText>
                </PharmacContainer>
                <PharmacH6 paddingMediumTop>
                  {t('customer.kelaNumber', { ns: 'extranet' })}
                </PharmacH6>
                <PharmacContainer
                  marginSmallTop
                  marginMediumBottom
                >
                  <PharmacText>{userCustomer?.kelaNumber || '-'}</PharmacText>
                </PharmacContainer>
              </PharmacColumn>
            </ColumnContainer>

            <ColumnContainer>
              <PharmacColumn>
                <PharmacH6>{t('customer.doseDeliveryEmailAddress', { ns: 'extranet' })}</PharmacH6>
                <PharmacContainer
                  marginSmallTop
                  marginMediumBottom
                >
                  <PharmacText>{userCustomer?.doseDeliveryEmailAddress || '-'}</PharmacText>
                </PharmacContainer>
                <PharmacH6 paddingMediumTop>
                  {t('customer.doseDeliveryPhoneNumber', { ns: 'extranet' })}
                </PharmacH6>
                <PharmacContainer
                  marginSmallTop
                  marginMediumBottom
                >
                  <PharmacText>{userCustomer?.doseDeliveryPhoneNumber || '-'}</PharmacText>
                </PharmacContainer>
              </PharmacColumn>
            </ColumnContainer>
          </InfoColumnContainer>
        </PharmacFieldset>
      )}
      {(userCustomer?.type as unknown as string) === 'pharmacy' && <ResetApiKeyInput />}
    </>
  )
}
